import * as amplitude from '@amplitude/analytics-browser';

import type {
  IRelatedTransactionsEventTracking,
  ITransferActionTrackEvent,
  ITransferDetailsTrackEvent,
} from '@app/dashboard/types/innerLayout/balance_and_transfers/single-transfer';
import { TRANSFER_ACTIONS } from '@app/dashboard/constants/balance_and_transfers/single-transfer';
import {
  BALANCE_TRANSFERS_EVENTS,
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { getCurrentPath } from '@app/dashboard/utils/getCurrentPath';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';

import {
  addToLocalStorage,
  getFromLocalStorage,
} from '@app/dashboard/utils/manageLocalStorage';

export function getSharedEventProperties({
  transferId,
  status,
  currency,
}: Record<string, string>) {
  return {
    [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.TRANSFER_ID]: transferId,
    [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.STATUS]: status,
    [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.CURRENCY]: currency,
  };
}

export const handleStoreLastAction = (action: string) => {
  addToLocalStorage(
    LOCAL_STORAGE_KEYS.BALANCE_AND_TRANSFERS_LAST_ACTION,
    action
  );
};

export const getLastActionFromLocalStorage = () => {
  return getFromLocalStorage(
    LOCAL_STORAGE_KEYS.BALANCE_AND_TRANSFERS_LAST_ACTION
  );
};

export const transferActionsEventTracking = ({
  action,
  sharedProperties,
  extraEventProperties = {},
}: ITransferActionTrackEvent) => {
  const eventProperties = {
    ...getSharedEventProperties(sharedProperties),
    [SHARED_EVENTS_PARAMS.PAGE]: getCurrentPath(),
    ...extraEventProperties,
  };

  const eventName = {
    [TRANSFER_ACTIONS.DOWNLOAD_PROOF]:
      BALANCE_TRANSFERS_EVENTS.TRANSFER_DETAIL_PAGE.PROOF_DOWNLOADED,
    [TRANSFER_ACTIONS.DOWNLOAD_TRANSACTIONS_CSV]:
      BALANCE_TRANSFERS_EVENTS.TRANSFER_DETAIL_PAGE.TRANSACTIONS_CSV_DOWNLOADED,
    [TRANSFER_ACTIONS.DOWNLOAD_TRANSACTIONS_PDF]:
      BALANCE_TRANSFERS_EVENTS.TRANSFER_DETAIL_PAGE.TRANSACTIONS_PDF_DOWNLOADED,
  };

  amplitude.track(eventName[action], eventProperties);
  handleStoreLastAction(eventName[action]);
};

export const TransferViewEventTracking = ({
  transferId,
  status,
  currency,
  netAmount,
  settlementType,
  timelineSteps,
  entryPoint,
}: ITransferDetailsTrackEvent) => {
  amplitude.track(
    BALANCE_TRANSFERS_EVENTS.TRANSFER_DETAIL_PAGE.VIEW_TRANSFER_DETAIL_PAGE,
    {
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.ENTRY_POINT]: entryPoint,
      ...getSharedEventProperties({
        transferId: transferId || '',
        status: status || '',
        currency: currency || '',
      }),
      [SHARED_EVENTS_PARAMS.PAGE]: getCurrentPath(),
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.NET_AMOUNT]: netAmount,
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.TRANSFER_DETAIL_PAGE
        .SETTLEMENT_TYPE]: settlementType,
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.TRANSFER_DETAIL_PAGE.TIMELINE_STEPS]:
        timelineSteps,
    }
  );
};

export const TransferViewFailedEventTracking = ({
  entryPoint,
  transferId,
  status,
  currency,
  bankDestination,
  timelineType,
}: Omit<
  ITransferDetailsTrackEvent,
  'netAmount' | 'settlementType' | 'timelineSteps'
> & {
  bankDestination: string;
  timelineType: string;
}) => {
  amplitude.track(
    BALANCE_TRANSFERS_EVENTS.TRANSFER_DETAIL_PAGE.FAILED_DETAIL_VIEWED,
    {
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.ENTRY_POINT]: entryPoint,
      [SHARED_EVENTS_PARAMS.PAGE]: getCurrentPath(),
      ...getSharedEventProperties({
        transferId: transferId || '',
        status: status || '',
        currency: currency || '',
      }),
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.TRANSFER_DETAIL_PAGE
        .BANK_DESTINATION]: bankDestination,
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.TIMELINE_TYPE]: timelineType,
    }
  );
};

export const viewRelatedTransactionsEventTracking = ({
  transferId,
  status,
  currency,
  transactionCount,
}: IRelatedTransactionsEventTracking) => {
  const eventName =
    BALANCE_TRANSFERS_EVENTS.TRANSFER_DETAIL_PAGE
      .VIEW_RELATED_TRANSACTIONS_CLICKED;

  amplitude.track(eventName, {
    [BALANCE_TRANSFERS_EVENTS_PROPERTIES.TRANSFER_DETAIL_PAGE
      .TRANSACTION_COUNT]: transactionCount,
    ...getSharedEventProperties({
      transferId,
      status,
      currency,
    }),
  });

  handleStoreLastAction(eventName);
};
